import { useMemo } from "react";

import { useUser } from "@/context/user-context";
import { useActiveOrganization, useIsOrgOwner } from "@/hooks/organizations";
import useActiveWorkspace from "@/hooks/useActiveWorkspace";
import useIsActiveWorkspaceAdmin from "@/hooks/useIsActiveWorkspaceAdmin";
import { useWorkspaces } from "@/queries";
import { useOrganizations } from "@/queries/organizations";
import { useCurrentUser } from "@/queries/users";
import { useGetWorkspaceInvites } from "@/queries/workspace";

export const useUserSectionData = () => {
  const { user, workspaceMembers } = useUser();

  // Workspace/org related hooks
  const isWorkspaceAdmin = useIsActiveWorkspaceAdmin();
  const isOrgOwner = useIsOrgOwner();
  const canInvite = isWorkspaceAdmin || isOrgOwner;

  const organizationsQuery = useOrganizations();
  const { data: activeOrganization } = useActiveOrganization();
  const workspacesQuery = useWorkspaces();
  const activeWorkspace = useActiveWorkspace();
  const currentUser = useCurrentUser();
  const defaultOrganizationId = currentUser.data?.default_organization_id;

  const invitesQuery = useGetWorkspaceInvites({
    invitee_email: currentUser.data?.email,
    status: "pending",
  });

  const organizationIds = useMemo(
    () => workspacesQuery.data?.workspaces.map(workspace => workspace.organization_id) || [],
    [workspacesQuery.data?.workspaces],
  );

  const organizations = useMemo(
    () =>
      organizationsQuery.data?.items.filter(organization =>
        organizationIds.includes(organization.id),
      ) || [],
    [organizationsQuery.data?.items, organizationIds],
  );

  const workspaceInvites = useMemo(
    () => invitesQuery.data?.items || [],
    [invitesQuery.data?.items],
  );

  const userRole = useMemo(() => {
    const currentMember = workspaceMembers.find(member => member.email === user?.email);
    return currentMember?.is_admin ? "Admin" : "Member";
  }, [workspaceMembers, user?.email]);

  const getDefaultWorkspaceId = (organizationId: number) => {
    const orgMember = currentUser.data?.memberships.organizations.find(
      member => member.organization_id === organizationId,
    );
    return orgMember?.default_workspace_id;
  };

  return {
    user,
    activeOrganization,
    activeWorkspace,
    organizations,
    workspaceInvites,
    userRole,
    canInvite,
    isWorkspaceAdmin,
    isOrgOwner,
    defaultOrganizationId,
    workspacesQuery,
    getDefaultWorkspaceId,
  };
};
